import React from "react";
import { ApolloClient } from "apollo-client";
import { InMemoryCache } from "apollo-cache-inmemory";
import { RestLink } from "apollo-link-rest";
import { ApolloLink } from "apollo-link";
import { onError } from "apollo-link-error";
import { setContext } from "apollo-link-context";
import { CONSUMER_SESSION_AUTH } from "./constants";
import { ApolloProvider } from "@apollo/react-hooks";
import { logger as LOGGER } from "./utils";
import { v4 as uuidv4 } from "uuid";

const CONSUMER_NOAUTH_OPERATIONS = [
  "postVerifyPhone",
  "postVerifyPhoneCode",
  "getAppointment",
  "getTestResult",
  "postLookupAppointment"
];

const GGT_CORRELATION_ID = "ggt-correlation-id";

const AuthorizedApolloProvider = ({ children }) => {
  const GLOBAL_CACHE = new InMemoryCache();

  /**
   * Error function
   *
   * @param {*} err
   */
  const ErrorFunc = err => {
    console.log(err);
  };

  /**
   * Setup REST link
   */
  const restLink = new RestLink({
    uri: `${process.env.REACT_APP_API_HOST}/api`,
    endpoints: {
      v1: `${process.env.REACT_APP_API_HOST}/api`,
      v2: `${process.env.REACT_APP_API_HOST}/api/v2`,
      chat: `${process.env.REACT_APP_AMZ_CONNECT_API_GATEWAY}`,
      availity_v1: `${process.env.REACT_APP_AVAILITY_API_HOST}/api/epdm/es/public/v1`
    }
  });

  /**
   * Handle Errors
   */
  const errorLink = onError(
    ({ graphQLErrors, networkError, operation, response }) => {
      const { operationName } = operation;
      if (graphQLErrors)
        graphQLErrors.map(({ message, locations, path }) =>
          LOGGER.error(
            `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
          )
        );
      if (networkError) {
        if (
          networkError.statusCode === 401 &&
          CONSUMER_NOAUTH_OPERATIONS.indexOf(operationName) === -1
        ) {
          sessionStorage.removeItem(CONSUMER_SESSION_AUTH);
          window.location.replace("/");
        }

        const ERROR_MESSAGE = networkError.result;

        if (ERROR_MESSAGE) {
          LOGGER.error(ERROR_MESSAGE);
          ErrorFunc(ERROR_MESSAGE);
        }

        const correlationId = operation.getContext()["headers"][
          GGT_CORRELATION_ID
        ];

        LOGGER.error(
          `Http Error to ${operationName} - ${correlationId} Network error: ${networkError}`,
          {
            response,
            networkError,
            [GGT_CORRELATION_ID]: correlationId
          }
        );
      }
    }
  );

  /**
   * Get auth0 token (not currently necessary)
   */
  const withTokenLink = setContext(() => ({
    auth0Token: ""
  }));

  /**
   * Add auth headers
   */
  const authLink = setContext((_, { headers, auth0Token }) => {
    return {
      headers: {
        ...headers,
        ...(auth0Token ? { authorization: `Bearer ${auth0Token}` } : {})
      }
    };
  });

  const loggerLink = new ApolloLink((operation, forward) => {
    const { variables, operationName } = operation;
    // Generate the correlationId
    const correlationId = uuidv4();

    // Set the context here
    operation.setContext(({ headers }) => ({
      headers: {
        [GGT_CORRELATION_ID]: correlationId,
        ...headers
      }
    }));

    LOGGER.info(`Request to ${operationName} - ${correlationId}`, {
      variables,
      operationName,
      [GGT_CORRELATION_ID]: correlationId
    });
    return forward(operation).map(result => {
      LOGGER.info(`Response to ${operationName} - ${correlationId}`, {
        data: result.data,
        [GGT_CORRELATION_ID]: correlationId
      });
      return result;
    });
  });

  /**
   * Create client
   */
  const apolloClient = new ApolloClient({
    link: ApolloLink.from([
      loggerLink,
      errorLink,
      withTokenLink,
      authLink,
      restLink
    ]),
    cache: GLOBAL_CACHE
  });

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};

export { AuthorizedApolloProvider };
